<template>
  <div>
    <ValidationObserver ref="observer"
                        v-slot="{invalid}"
                        tag="div">
      <div class="justify-content-left mt-1 dp02 p-3">
        <div class="col-lg-12">
          <h2 v-if="!isEdit">
            {{ $t("CreateEditTemplateStep.HeaderIsNotEdit") }}
          </h2>
          <h2 v-else>
            {{ $t("CreateEditTemplateStep.HeaderIsEdit") }}
          </h2>
        </div>
        <div class="col-lg-12 mt-3 h-100"
             style="min-height:20vh">
          <h2>{{ $t('entities.Template.SingularCs') }}</h2>
          <ckeditor v-model="template"
                    :editor="editor"
                    @ready="onReady"
                    :config="editorConfig" />
        </div>
        <div class="col-lg-12 mt-3 h-100"
             style="min-height:20vh">
          <h2>{{ $t('entities.Template.SingularEn') }}</h2>
          <ckeditor v-model="templateEn"
                    :editor="editor"
                    @ready="onReady"
                    :config="editorConfig" />
        </div>
        <div class="col-lg-4 mt-2">
          <h2 v-if="!isEdit">
            {{ $t("CreateEditTemplateStep.AddNote") }}
          </h2>
          <h2 v-else>
            {{ $t("CreateEditTemplateStep.EditNote") }}
          </h2>
          <ValidationProvider
            name="note"
            rules="required"
            v-slot="{errors, classes}">
            <b-form-input type="text"
                          v-model="note"
                          :class="{...classes, myInput: true}"
                          placeholder="Note" />
            <p class="myInputError">
              {{ errors[0] }}
            </p>
          </ValidationProvider>
        </div>
        <div class="col-4 mt-2">
          <h3>
            {{ $t('fields.risk') }}
          </h3>
          <TemplateRiskInput v-model="risk" />
        </div>
        <div class="col-lg-12 mt-2">
          <b-button
            v-if="!isEdit"
            class="myButton align-middle"
            @click="submit">
            {{ $t("CreateEditTemplateStep.CreateTemplate") }}
          </b-button>
          <b-button
            v-if="isEdit && customerCount === 0"
            class="myButton align-middle"
            @click="submit">
            {{ $t("CreateEditTemplateStep.EditTemplate") }}
          </b-button>
          <b-button
            v-if="isEdit && customerCount !== 0"
            class="myButton mr-2 align-middle"
            @click="$refs.emailModal.show()">
            {{ $t("CreateEditTemplateStep.EditTemplateWithNotify", {1 : customerCount}) }}
          </b-button>
          <b-button
            v-if="isEdit && customerCount !== 0"
            class="myButton mr-2 align-middle"
            @click="submit">
            {{ $t("CreateEditTemplateStep.EditTemplateWithoutNotify") }}
          </b-button>
          <b-spinner
            v-if="requestSent"
            variant="primary"
            class="align-middle ml-3"
            label="Spinning" />
        </div>
      </div>
    </ValidationObserver>
    <b-modal id="modal-center"
             ref="emailModal"
             centered
             :static="true">
      <template #modal-header>
        <div class="d-flex justify-content-center w-100 text-center">
          <h3>
            {{ $t('common.NotificationEmail') }}
          </h3>
        </div>
      </template>
      <ValidationObserver
        ref="emailObserver"
        v-slot="{invalid}"
        tag="form">
        <div class="row">
          <div class="col-lg-2">
            <label for="emailBody">{{ $t('common.Message') }}:</label>
          </div>
          <div class="col-lg-10">
            <ValidationProvider
              name="emailBody"
              rules="required"
              v-slot="{errors, classes}">
              <b-form-textarea
                v-model="emailBody"
                :class="{...classes, myInput: true}"
                no-resize
                rows="7" />
              <p class="myInputError mt-2">
                {{ errors[0] }}
              </p>
            </ValidationProvider>
          </div>
        </div>
      </ValidationObserver>
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="secondary"
          @click="emailBody = ''; $refs.emailModal.hide();">
          {{ $t('common.Cancel') }}
        </b-button>
        <b-button
          variant="success"
          @click="submit();">
          {{ $t('common.SendAndSave') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex';
import DecoupledEditor from '@ckeditor/ckeditor5-editor-decoupled/src/decouplededitor';
import EssentialsPlugin from '@ckeditor/ckeditor5-essentials/src/essentials';
import UploadAdapterPlugin from '@ckeditor/ckeditor5-adapter-ckfinder/src/uploadadapter';
import AutoformatPlugin from '@ckeditor/ckeditor5-autoformat/src/autoformat';
import HeadingPlugin from '@ckeditor/ckeditor5-heading/src/heading';
import FontFamilyPlugin from '@ckeditor/ckeditor5-font/src/fontfamily';
import FontSizePlugin from '@ckeditor/ckeditor5-font/src/fontsize';
import FontColorPlugin from '@ckeditor/ckeditor5-font/src/fontcolor';
import FontBackgroundColorPlugin from '@ckeditor/ckeditor5-font/src/fontbackgroundcolor';
import BoldPlugin from '@ckeditor/ckeditor5-basic-styles/src/bold';
import ItalicPlugin from '@ckeditor/ckeditor5-basic-styles/src/italic';
import UnderlinePlugin from '@ckeditor/ckeditor5-basic-styles/src/underline';
import StrikethroughPlugin from '@ckeditor/ckeditor5-basic-styles/src/strikethrough';
import AlignmentPlugin from '@ckeditor/ckeditor5-alignment/src/alignment';
import ListPlugin from '@ckeditor/ckeditor5-list/src/list';
import IndentPlugin from '@ckeditor/ckeditor5-indent/src/indent';
import IndentBlockPlugin from '@ckeditor/ckeditor5-indent/src/indentblock';
import LinkPlugin from '@ckeditor/ckeditor5-link/src/link';
import BlockQuotePlugin from '@ckeditor/ckeditor5-block-quote/src/blockquote';
import EasyImagePlugin from '@ckeditor/ckeditor5-easy-image/src/easyimage';
import ImagePlugin from '@ckeditor/ckeditor5-image/src/image';
import ImageCaptionPlugin from '@ckeditor/ckeditor5-image/src/imagecaption';
import ImageStylePlugin from '@ckeditor/ckeditor5-image/src/imagestyle';
import ImageToolbarPlugin from '@ckeditor/ckeditor5-image/src/imagetoolbar';
import ImageUploadPlugin from '@ckeditor/ckeditor5-image/src/imageupload';
import TablePlugin from '@ckeditor/ckeditor5-table/src/table';
import TableToolbarPlugin from '@ckeditor/ckeditor5-table/src/tabletoolbar';
import MediaEmbedPlugin from '@ckeditor/ckeditor5-media-embed/src/mediaembed';
import ParagraphPlugin from '@ckeditor/ckeditor5-paragraph/src/paragraph';
import HorizontalLinePlugin from '@ckeditor/ckeditor5-horizontal-line/src/horizontalline';
import TemplateRiskInput from '@components/input/TemplateRiskInput';
import Adapter from '@utils/base64uploadadapter';

export default {
  name: 'CreateEditTemplateStep',
  components: {
    TemplateRiskInput
  },
  data() {
    return {
      template: '',
      templateEn: '',
      editor: DecoupledEditor,
      editorConfig: {
        plugins: [
          EssentialsPlugin,
          AutoformatPlugin,
          HeadingPlugin,
          FontFamilyPlugin,
          FontSizePlugin,
          FontColorPlugin,
          FontBackgroundColorPlugin,
          BoldPlugin,
          ItalicPlugin,
          UnderlinePlugin,
          StrikethroughPlugin,
          AlignmentPlugin,
          ListPlugin,
          IndentPlugin,
          IndentBlockPlugin,
          LinkPlugin,
          BlockQuotePlugin,
          ImagePlugin,
          ImageCaptionPlugin,
          ImageStylePlugin,
          ImageToolbarPlugin,
          EasyImagePlugin,
          ImageUploadPlugin,
          TablePlugin,
          TableToolbarPlugin,
          MediaEmbedPlugin,
          ParagraphPlugin,
          UploadAdapterPlugin,
          HorizontalLinePlugin
        ],
        toolbar: [
          'heading',
          '|',
          'fontFamily',
          'fontSize',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'alignment',
          '|',
          'numberedList',
          'bulletedList',
          '|',
          'outdent',
          'indent',
          '|',
          'link',
          'blockQuote',
          'imageUpload',
          'insertTable',
          'mediaEmbed',
          '|',
          'undo',
          'redo',
          '|',
          'horizontalLine'
        ],
        fontFamily: {
          supportAllValues: true
        },
        fontSize: {
          options: [
            'tiny',
            'small',
            'default',
            'big',
            'huge'
          ]
        },
        image: {
          toolbar: [
            'imageStyle:full',
            'imageStyle:side',
            '|',
            'imageTextAlternative'
          ]
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        }
      },
      isEdit: false,
      requestSent: false,
      note: '',
      risk: 1
    };
  },
  mounted() {
    this.isEdit = this.getTemplateId() !== null;
  },
  watch: {
    template(val) {
      this.setTemplate(val);
    },
    templateEn(val) {
      this.setTemplateEn(val);
    },
    note(val) {
      this.setNote(val);
    },
    risk(val) {
      this.setRisk(val);
    }
  },
  computed: {
    emailBody: {
      get() {
        return this.$store.state.createTemplate.emailBody;
      },
      set(val) {
        this.setEmailBody(val);
      }
    },
    ...mapState('createTemplate', ['customerCount']),
  },
  methods: {
    ...mapActions({
      createRequest: 'createTemplate/createRequest',
      editRequest: 'createTemplate/editRequest'
    }),
    ...mapMutations({
      setTemplate: 'createTemplate/setTemplate',
      setTemplateEn: 'createTemplate/setTemplateEn',
      setNote: 'createTemplate/setNote',
      setRisk: 'createTemplate/setRisk',
      setEmailBody: 'createTemplate/setEmailBody'
    }),
    ...mapGetters({
      getTemplate: 'createTemplate/getTemplate',
      getTemplateEn: 'createTemplate/getTemplateEn',
      getTemplateId: 'createTemplate/getTemplateId',
      getNote: 'createTemplate/getNote',
      getRisk: 'createTemplate/getRisk'
    }),
    onReady(editor) {
      // Insert the toolbar before the editable area.
      editor.ui
        .getEditableElement()
        .parentElement.insertBefore(
          editor.ui.view.toolbar.element,
          editor.ui.getEditableElement()
        );
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) => new Adapter(loader);
      if (this.getTemplate().length !== 0) {
        this.template = this.getTemplate();
        this.templateEn = this.getTemplateEn();
        this.note = this.getNote();
        this.risk = this.getRisk();
      }
    },
    createTemplate() {
      this.requestSent = true;
      this.createRequest()
        .then(() => {
          this.requestSent = false;
          this.$root.$bvToast.toast(this.$t('CreateEditTemplateStep.SuccessCreateToast.Body'), {
            title: this.$t('CreateEditTemplateStep.SuccessCreateToast.Title'),
            variant: 'success',
            toaster: 'b-toaster-top-right'
          });
          this.$router.push({ name: 'admin.templates.view' });
        })
        .catch(() => {
          this.requestSent = false;
          this.$root.$bvToast.toast(this.$t('CreateEditTemplateStep.FailCreateToast.Body'), {
            title: this.$t('CreateEditTemplateStep.FailCreateToast.Title'),
            variant: 'danger',
            toaster: 'b-toaster-top-right'
          });
        });
    },
    editTemplate() {
      this.requestSent = true;
      this.editRequest()
        .then(() => {
          this.requestSent = false;
          this.$root.$bvToast.toast(this.$t('CreateEditTemplateStep.SuccessEditToast.Body'), {
            title: this.$t('CreateEditTemplateStep.SuccessEditToast.Title'),
            variant: 'success',
            toaster: 'b-toaster-top-right'
          });
          this.$router.push({ name: 'admin.templates.view' });
        })
        .catch(() => {
          this.requestSent = false;
          this.$root.$bvToast.toast(this.$t('CreateEditTemplateStep.FailEditToast.Body'), {
            title: this.$t('CreateEditTemplateStep.FailEditToast.Title'),
            variant: 'danger',
            toaster: 'b-toaster-top-right'
          });
        });
    },
    async submit() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        if (this.isEdit) {
          this.editTemplate();
        } else {
          this.createTemplate();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
</style>
